/**
 * Invitations Store (Zustand)
 * Manages all C2 invitations in memory
 * Handles real-time updates from WebSocket
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { Invitation } from '../types/invitations.types';

// Re-export for consumers that import Invitation from the store
export type { Invitation };

interface InvitationsState {
  // Data storage
  invitations: Map<string, Invitation>;
  allInvitations: Invitation[];

  // Metadata
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  lastUpdatedAt: string | null;

  // Actions
  setInvitations: (invitations: Invitation[]) => void;
  addInvitation: (invitation: Invitation) => void;
  updateInvitation: (id: string, updates: Partial<Invitation>) => void;
  deleteInvitation: (id: string) => void;

  // Real-time updates from WebSocket
  handleInvitationUpdated: (updates: Partial<Invitation>) => void;

  // Selectors & Queries
  getInvitationById: (id: string) => Invitation | undefined;
  getInvitationsByStatus: (status: string) => Invitation[];
  getStats: () => {
    total: number;
    pending: number;
    accepted: number;
    declined: number;
    expired: number;
  };

  // Metadata updates
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setTotalCount: (count: number) => void;

  // Session management
  clearInvitations: () => void;
}

export const useInvitationsStore = create<InvitationsState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    invitations: new Map(),
    allInvitations: [],
    totalCount: 0,
    isLoading: false,
    error: null,
    lastUpdatedAt: null,

    // Set all invitations (full replace from backend refresh)
    setInvitations: (newInvitations: Invitation[]) => {
      set(() => {
        const invitationsMap = new Map<string, Invitation>();
        newInvitations.forEach((invitation) => {
          invitationsMap.set(invitation.id, invitation);
        });

        return {
          invitations: invitationsMap,
          allInvitations: newInvitations,
          totalCount: newInvitations.length,
          lastUpdatedAt: new Date().toISOString(),
        };
      });
    },

    // Add single invitation
    addInvitation: (invitation: Invitation) => {
      set((state) => {
        const newMap = new Map(state.invitations);
        newMap.set(invitation.id, invitation);
        const newAllInvitations = [...state.allInvitations, invitation];


        return {
          invitations: newMap,
          allInvitations: newAllInvitations,
          totalCount: newAllInvitations.length,
          lastUpdatedAt: new Date().toISOString(),
        };
      });
    },

    // Update single invitation
    updateInvitation: (id: string, updates: Partial<Invitation>) => {
      set((state) => {
        const existing = state.invitations.get(id);

        if (!existing) {
          const newInvitation = { ...updates, id } as Invitation;
          const newMap = new Map(state.invitations);
          newMap.set(id, newInvitation);
          const newAllInvitations = [...state.allInvitations, newInvitation];

          return {
            invitations: newMap,
            allInvitations: newAllInvitations,
            totalCount: newAllInvitations.length,
            lastUpdatedAt: new Date().toISOString(),
          };
        }

        const updated = { ...existing, ...updates };
        const newMap = new Map(state.invitations);
        newMap.set(id, updated);

        const newAllInvitations = state.allInvitations.map((i) =>
          i.id === id ? updated : i
        );


        return {
          invitations: newMap,
          allInvitations: newAllInvitations,
          lastUpdatedAt: new Date().toISOString(),
        };
      });
    },

    // Delete invitation
    deleteInvitation: (id: string) => {
      set((state) => {
        const newMap = new Map(state.invitations);
        newMap.delete(id);
        const newAllInvitations = state.allInvitations.filter(
          (i) => i.id !== id
        );


        return {
          invitations: newMap,
          allInvitations: newAllInvitations,
          totalCount: newAllInvitations.length,
          lastUpdatedAt: new Date().toISOString(),
        };
      });
    },

    // Handle real-time invitation update
    handleInvitationUpdated: (updates: Partial<Invitation>) => {
      if (!updates.id) return;
      get().updateInvitation(updates.id, updates);
    },

    // Get invitation by ID
    getInvitationById: (id: string) => {
      return get().invitations.get(id);
    },

    // Get invitations by status
    getInvitationsByStatus: (status: string) => {
      return get().allInvitations.filter((i) => i.status === status);
    },

    // Get statistics
    getStats: () => {
      const invitations = get().allInvitations;
      return {
        total: invitations.length,
        pending: invitations.filter((i) => i.status === 'pending').length,
        accepted: invitations.filter((i) => i.status === 'accepted').length,
        declined: invitations.filter((i) => i.status === 'declined').length,
        expired: invitations.filter((i) => i.status === 'expired').length,
      };
    },

    // Metadata setters
    setLoading: (loading: boolean) => {
      set({ isLoading: loading });
    },

    setError: (error: string | null) => {
      set({ error });
    },

    setTotalCount: (count: number) => {
      set({ totalCount: count });
    },

    clearInvitations: () => {
      set({
        invitations: new Map(),
        allInvitations: [],
        totalCount: 0,
        lastUpdatedAt: null,
      });
    },
  }))
);

// Debug logging
if (process.env.NODE_ENV === 'development') {
  useInvitationsStore.subscribe(
    (state) => state.allInvitations,
    (invitations) => {
    }
  );
}

export default useInvitationsStore;
